import { ReactNode, useMemo, useState } from "react";

import { cn } from "../utls/utils";
import Typography from "./typography";

export type InfoRowLongTextAccent = "terracotta" | "brown" | "beige";

const accentBadgeClasses: Record<InfoRowLongTextAccent, string> = {
  terracotta: "bg-terracotta-50 text-terracotta-700 border-terracotta-200",
  brown: "bg-brown-50 text-brown-700 border-brown-200",
  beige: "bg-beige-50 text-brown-600 border-beige-200",
};

export interface InfoRowLongTextProps {
  /**
   * Primary label displayed at the top of the row.
   */
  label: ReactNode;
  /**
   * Long-form value rendered below the label. Supports multi-paragraph content.
   */
  text: string;
  /**
   * Optional helper text displayed under the label.
   */
  helperText?: ReactNode;
  /**
   * Optional call-to-action or metadata surfaced on the right-hand side.
   */
  actions?: ReactNode;
  /**
   * Decorative icon displayed next to the label.
   */
  icon?: ReactNode;
  /**
   * Controls the accent tone of the label badge.
   */
  accent?: InfoRowLongTextAccent;
  /**
   * Maximum characters displayed when the content is collapsed.
   */
  previewCharacterCount?: number;
  /**
   * Whether the row should be expanded on initial render.
   */
  initiallyExpanded?: boolean;
  /**
   * Optional class names to adjust the outer container spacing.
   */
  className?: string;
}

export function InfoRowLongText({
  label,
  text,
  helperText,
  actions,
  icon,
  accent = "terracotta",
  previewCharacterCount = 220,
  initiallyExpanded = false,
  className,
}: InfoRowLongTextProps) {
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);

  const sanitizedText = useMemo(() => text.trim(), [text]);
  const shouldCollapse = sanitizedText.length > previewCharacterCount;
  const displayText = useMemo(() => {
    if (isExpanded || !shouldCollapse) {
      return sanitizedText;
    }
    return `${sanitizedText.slice(0, previewCharacterCount).trimEnd()}…`;
  }, [isExpanded, sanitizedText, previewCharacterCount, shouldCollapse]);

  return (
    <div
      className={cn(
        "w-full rounded-lg border border-cream-200 bg-cream-50 p-4 shadow-sm",
        className
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-start gap-2">
            {icon ? <span className="mt-0.5 text-terracotta-500">{icon}</span> : null}
            <span
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide",
                accentBadgeClasses[accent]
              )}
            >
              {label}
            </span>
          </div>
          {helperText ? (
            <Typography
              variant="caption"
              className="text-brown-600"
            >
              {helperText}
            </Typography>
          ) : null}
        </div>
        {actions ? <div className="flex-shrink-0">{actions}</div> : null}
      </div>

      <div className="mt-4 space-y-3">
        <Typography variant="body2" className="whitespace-pre-wrap text-brown-700">
          {displayText}
        </Typography>
        {shouldCollapse ? (
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="text-sm font-medium text-terracotta-600 transition-colors hover:text-terracotta-700"
            aria-expanded={isExpanded}
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default InfoRowLongText;
